import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState } from 'react';
import { mod } from 'react-swipeable-views-core';
import { Container } from '../../../components/Image/Carousel/components/Container.js';
import { Counter } from '../../../components/Image/Carousel/components/Counter.js';
import { CounterContainer } from '../../../components/Image/Carousel/components/CounterContainer.js';
import { Image } from '../../../components/Image/Carousel/components/Image.js';
import { Pipe } from '../../../components/Image/Carousel/components/Pipe.js';
import { VirtualizedSwipeableViews } from '../../../components/Image/Carousel/components/VirtualizedSwipeableViews.js';
import { NextControl, PreviousControl } from '../../../components/Image/Controls/index.js';
const springConfig = {
  duration: '0.45s',
  easeFunction: 'cubic-bezier(0.15, 0.3, 0.25, 1)',
  delay: '0.05s'
};
export const Carousel = ({
  alt,
  images: imagesProperty,
  rounded = false,
  children,
  onClick,
  onClickPrev,
  onClickNext,
  slideIndex,
  onChangeSlideIndex,
  isLCPOptimized = false,
  isFinite,
  indentImage,
  hasCounter
}) => {
  const {
    t
  } = useTranslation('imageCarousel');
  const [hovering, setHovering] = useState(false);
  const images = useMemo(() => {
    return imagesProperty.map(image => typeof image === 'string' ? {
      url: image,
      provider: ''
    } : image);
  }, [imagesProperty]);
  const handleMouseEnter = useCallback(() => {
    setHovering(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setHovering(false);
  }, []);
  const slideRenderer = useCallback(({
    index,
    key
  }) => {
    return _jsx(Image, {
      image: images[mod(index, images.length)].url,
      priority: isLCPOptimized,
      layout: "fill",
      objectFit: "cover",
      aspectRatio: "16/9",
      indent: indentImage,
      alt: index === 0 ? alt : undefined
    }, key);
  }, [images, alt, isLCPOptimized, indentImage]);
  if (images.length === 0) {
    return null;
  }
  const imagesCount = images.length;
  const activeImage = images[slideIndex];
  const activeImagePosition = slideIndex + 1;
  return _jsxs(Container, {
    rounded: rounded,
    onClick: onClick,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onTouchEnd: handleMouseLeave,
    children: [_jsx(CounterContainer, {
      children: hasCounter && _jsx(Counter, {
        children: activeImage.provider ? _jsxs(_Fragment, {
          children: [t('pictureLabel', {
            label: t('counter', {
              imagePosition: activeImagePosition,
              imageCount: imagesCount
            })
          }), _jsx(Pipe, {}), t('providerLabel', {
            source: activeImage.provider
          })]
        }) : t('counter', {
          imagePosition: activeImagePosition,
          imageCount: imagesCount
        })
      })
    }), images.length === 1 ? _jsx(Image, {
      priority: isLCPOptimized,
      image: images[0].url ?? '',
      layout: "fill",
      objectFit: "cover",
      aspectRatio: "16/9",
      indent: indentImage,
      alt: alt
    }) : _jsxs(_Fragment, {
      children: [_jsx(VirtualizedSwipeableViews, {
        index: slideIndex,
        onChangeIndex: onChangeSlideIndex,
        overscanSlideBefore: 1,
        overscanSlideAfter: 1,
        slideRenderer: slideRenderer,
        springConfig: springConfig,
        enableMouseEvents: true
      }), _jsx(PreviousControl, {
        hidden: isFinite && slideIndex === 0,
        highlighted: hovering,
        onClick: onClickPrev
      }), _jsx(NextControl, {
        hidden: isFinite && slideIndex + 1 === images.length,
        highlighted: hovering,
        onClick: onClickNext
      })]
    }), children]
  });
};